import { useMemo } from 'react';
import { useRealtimeStore } from '@/features/realtime/realtimeStore';
import type { TelemetryPoint } from '@monitor/shared';
import { BOARD_ONLINE_MS } from '@/lib/boardStatus';

interface SiteSnapshot {
  latest: TelemetryPoint | null;
  /** Highest power across all sensors at this site (W) */
  totalPower: number;
  /** Sum of latest energy across sensors (kWh) */
  totalEnergy: number;
  /** Max temperature across sensors (°C) */
  maxTemp: number;
  /** Number of sensors that have any data */
  liveSensorCount: number;
  /** ISO time of most recent reading across sensors */
  lastSeenAt: string | null;
  /** True when the most recent reading is within the shared online window
   *  (BOARD_ONLINE_MS) — same threshold the board status uses. */
  online: boolean;
}

interface Result {
  byId: Record<number, SiteSnapshot | null>;
  isLoading: boolean;
}

/**
 * Aggregates per-site snapshots from the global realtime store.
 * Replaces HTTP polling — relies on `useGlobalRealtime` to keep the store fresh.
 */
/**
 * @param primaryIds When provided (non-empty), power/energy are summed from the
 *   board's PRIMARY meter only — avoids double-counting a board that carries
 *   both a PZEM and a KWS on the same circuit. Falls back to all sensors while
 *   the sensor list is still loading.
 */
export function useSitesOverview(siteIds: number[], primaryIds?: Set<number>): Result {
  const bySite = useRealtimeStore((s) => s.bySite);

  const byId = useMemo<Record<number, SiteSnapshot | null>>(() => {
    const map: Record<number, SiteSnapshot | null> = {};
    const now = Date.now();
    const usePrimary = primaryIds != null && primaryIds.size > 0;
    for (const siteId of siteIds) {
      const site = bySite[siteId];
      if (!site || Object.keys(site.bySensor).length === 0) {
        map[siteId] = null;
        continue;
      }
      let totalPower = 0;
      let totalEnergy = 0;
      let maxTemp = 0;
      let liveSensorCount = 0;
      let latestTime: number | null = null;
      let latestPoint: TelemetryPoint | null = null;

      for (const [sidStr, sensor] of Object.entries(site.bySensor)) {
        const p = sensor.latest;
        if (!p) continue;
        liveSensorCount += 1;
        if (!usePrimary || primaryIds!.has(Number(sidStr))) {
          if (p.power != null) totalPower += p.power;
          if (p.energy != null) totalEnergy += p.energy;
        }
        if (p.temperature != null && p.temperature > maxTemp) maxTemp = p.temperature;
        const t = new Date(p.time).getTime();
        if (latestTime == null || t > latestTime) {
          latestTime = t;
          latestPoint = p;
        }
      }

      if (liveSensorCount === 0) {
        map[siteId] = null;
        continue;
      }

      map[siteId] = {
        latest: latestPoint,
        totalPower,
        totalEnergy,
        maxTemp,
        liveSensorCount,
        lastSeenAt: latestPoint?.time ?? null,
        online: latestTime != null && now - latestTime < BOARD_ONLINE_MS,
      };
    }
    return map;
  }, [bySite, siteIds, primaryIds]);

  return { byId, isLoading: false };
}
